<template>
  <div class="user-list-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Main Content -->
    <CRow>
      <CCol>
        <CCard class="main-card">
          <CCardHeader class="professional-header">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="header-title">
                <h5 class="mb-0">
                  <i class="fas fa-users me-2"></i>
                  <strong>User Management</strong>
                </h5>
                <small class="text-muted">Manage system users and permissions</small>
              </div>
              <div class="header-actions">
                <CBadge color="info" class="result-counter">
                  {{ items.length }} {{ items.length === 1 ? 'User' : 'Users' }}
                </CBadge>
              </div>
            </div>
          </CCardHeader>

          <CCardBody>
            <!-- Loading State -->
            <div v-if="loading && items.length === 0" class="loading-state">
              <div class="text-center py-5">
                <CSpinner color="primary" />
                <p class="text-muted mt-3">Loading users...</p>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="!loading && items.length === 0" class="empty-state">
              <div class="text-center py-5">
                <i class="fas fa-users fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No users found</h5>
                <p class="text-muted">Get started by adding your first user</p>
                <CButton
                  v-if="canAdd"
                  color="primary"
                  @click="addNew"
                  class="mt-2"
                >
                  <i class="fas fa-plus me-2"></i>
                  Add New User
                </CButton>
              </div>
            </div>

            <!-- Data View (Mobile + Desktop) -->
            <div v-else>
            <!-- Mobile Card View -->
            <div class="mobile-view d-md-none">
              <div v-for="(item, index) in items" :key="item.id" class="mobile-card">
                <div class="mobile-card-header" @click="onEdit(item)">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <div class="user-number text-muted mb-1">
                        <small>#{{ index + 1 }}</small>
                      </div>
                      <h6 class="user-name mb-1">
                        <i class="fas fa-user me-2 text-primary"></i>
                        {{ item.fullName }}
                      </h6>
                      <div class="user-details">
                        <div v-if="item.email" class="text-muted mb-1">
                          <small>
                            <i class="fas fa-envelope me-1"></i>
                            {{ item.email }}
                          </small>
                        </div>
                        <div v-if="item.phone" class="text-muted mb-1">
                          <small>
                            <i class="fas fa-phone me-1"></i>
                            {{ item.phone }}
                          </small>
                        </div>
                        <div v-if="item.subCompanyName || (item.subCompany && item.subCompany.name)" class="text-muted mb-1">
                          <small>
                            <i class="fas fa-building me-1"></i>
                            {{ item.subCompanyName || item.subCompany.name }}
                          </small>
                        </div>
                        <div v-if="item.role" class="mt-2">
                          <CBadge :color="getRoleColor(item.role)">
                            {{ item.role }}
                          </CBadge>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mobile-card-footer">
                  <div class="d-flex gap-2">
                    <CButton
                      size="sm"
                      :color="canUpdate ? 'primary' : 'info'"
                      variant="outline"
                      @click="onEdit(item)"
                      class="flex-grow-1"
                    >
                      <i :class="canUpdate ? 'fas fa-edit me-1' : 'fas fa-eye me-1'"></i>
                      {{ canUpdate ? 'Edit' : 'View' }}
                    </CButton>
                    <CButton
                      v-if="canDelete"
                      size="sm"
                      color="danger"
                      variant="outline"
                      @click="showDeleteConfirmation(item)"
                    >
                      <i class="fas fa-trash me-1"></i>
                      Delete
                    </CButton>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop Table View -->
            <div class="desktop-view d-none d-md-block">
              <div class="table-responsive">
                <CDataTable
                  :items="items"
                  :fields="fields"
                  :hover="true"
                  :loading="loading"
                  column-filter
                  :items-per-page="20"
                  sorter
                  pagination
                  @row-clicked="onEdit"
                  class="professional-table"
                >
                  <template #show_index="{ index }">
                    <td class="text-center">
                      <span class="text-muted">#{{ index + 1 }}</span>
                    </td>
                  </template>

                  <template #fullName="{ item }">
                    <td>
                      <div class="d-flex align-items-center">
                        <i class="fas fa-user text-primary me-2"></i>
                        <strong>{{ item.fullName }}</strong>
                      </div>
                    </td>
                  </template>

                  <template #email="{ item }">
                    <td>
                      <span v-if="item.email" class="text-muted">
                        <i class="fas fa-envelope me-1"></i>
                        {{ item.email }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #phone="{ item }">
                    <td>
                      <span v-if="item.phone" class="text-muted">
                        <i class="fas fa-phone me-1"></i>
                        {{ item.phone }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #role="{ item }">
                    <td>
                      <CBadge v-if="item.role" :color="getRoleColor(item.role)">
                        {{ item.role }}
                      </CBadge>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #subCompanyName="{ item }">
                    <td>
                      <span v-if="item.subCompanyName || (item.subCompany && item.subCompany.name)" class="text-muted">
                        <i class="fas fa-building me-1"></i>
                        {{ item.subCompanyName || item.subCompany.name }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #show_details="{ item }">
                    <td class="text-end">
                      <CButton
                        :color="canUpdate ? 'primary' : 'info'"
                        variant="outline"
                        size="sm"
                        @click.stop="onEdit(item)"
                        class="me-1"
                      >
                        <i :class="canUpdate ? 'fas fa-edit me-1' : 'fas fa-eye me-1'"></i>
                        {{ canUpdate ? 'Edit' : 'View' }}
                      </CButton>
                      <CButton
                        v-if="canDelete"
                        color="danger"
                        variant="outline"
                        size="sm"
                        @click.stop="showDeleteConfirmation(item)"
                      >
                        <i class="fas fa-trash me-1"></i>
                        Delete
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </div>
            </div>
            </div>
          </CCardBody>

          <!-- Footer -->
          <CCardFooter class="card-footer">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="footer-actions">
                <CButton v-if="canAdd" color="primary" @click="addNew">
                  <i class="fas fa-plus me-2"></i>
                  Add New User
                </CButton>
                <CButton
                  color="success"
                  variant="outline"
                  @click="exportCSV"
                  class="ms-2"
                >
                  <i class="fas fa-file-csv me-2"></i>
                  Export CSV
                </CButton>
                <CButton
                  color="danger"
                  variant="outline"
                  @click="exportPDF"
                  class="ms-2"
                >
                  <i class="fas fa-file-pdf me-2"></i>
                  Export PDF
                </CButton>
              </div>
              <div class="footer-info">
                <small class="text-muted">
                  Total: {{ items.length }} {{ items.length === 1 ? 'user' : 'users' }}
                </small>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Delete Confirmation Modal -->
    <CModal :show.sync="warningModal" color="warning">
      <template #header>
        <h5 class="modal-title">
          <i class="fas fa-exclamation-triangle me-2"></i>
          Confirm Delete
        </h5>
      </template>
      <p>Are you sure you want to delete <strong>{{ itemToDelete.fullName }}</strong>?</p>
      <p class="text-muted mb-0">
        <small>This action cannot be undone.</small>
      </p>
      <template #footer>
        <CButton color="secondary" @click="warningModal = false">
          Cancel
        </CButton>
        <CButton color="danger" @click="onDeleteConfirmation">
          <i class="fas fa-trash me-2"></i>
          Delete
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ProfileApi from "../../lib/profileApi";
import html2pdf from "html2pdf.js";

export default {
  name: "UserList",
  data() {
    return {
      loading: true,
      items: [],
      infoList: [],
      fields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:60px",
          sorter: false,
          filter: false,
        },
        {
          key: "fullName",
          label: "Full Name",
          _style: "min-width:200px;"
        },
        {
          key: "email",
          label: "Email",
          _style: "min-width:200px;"
        },
        {
          key: "phone",
          label: "Phone",
          _style: "min-width:130px;"
        },
        {
          key: "role",
          label: "Role",
          _style: "min-width:120px;"
        },
        {
          key: "subCompanyName",
          label: "Sub Company",
          _style: "min-width:150px;"
        },
        {
          key: "show_details",
          label: "",
          _style: "width:180px",
          sorter: false,
          filter: false,
        },
      ],
      api: new ProfileApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  computed: {
    canView() {
      return this.$auth.hasSubModulePermission('USER', 'CanView');
    },
    canAdd() {
      return this.$auth.hasSubModulePermission('USER', 'CanAdd');
    },
    canDelete() {
      return this.$auth.hasSubModulePermission('USER', 'CanDelete');
    },
    canUpdate() {
      return this.$auth.hasSubModulePermission('USER', 'CanUpdate');
    }
  },
  mounted() {
    // Check view permission
    if (!this.canView) {
      this.$router.push({ name: 'Page403' });
      return;
    }
    this.refreshTable();
  },
  methods: {
    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    refreshTable() {
      this.loading = true;
      this.api
        .getProfileList()
        .then((response) => {
          console.log('=== [UserList] User list loaded ===');
          console.log('Total users:', response.result?.length || 0);

          // Map subCompany.name to subCompanyName for easier filtering
          this.items = (response.result || []).map(item => ({
            ...item,
            subCompanyName: item.subCompany?.name || item.subCompanyName || ''
          }));

          // Log test3@email.com user if exists
          const test3User = this.items.find(u => u.email === 'test3@email.com');
          if (test3User) {
            console.log('=== [UserList] test3@email.com found ===');
            console.log('User ID:', test3User.id);
            console.log('Full Name:', test3User.fullName);
            console.log('Email:', test3User.email);
            console.log('Role:', test3User.role);
            console.log('Full user object:', test3User);
          }

          this.loading = false;
        })
        .catch((error) => {
          this.toast("Error", "Failed to load users", "danger");
          this.loading = false;
        });
    },

    getRoleColor(role) {
      const roleLower = (role || '').toLowerCase();
      if (roleLower.includes('super')) return 'danger';
      if (roleLower.includes('admin')) return 'primary';
      if (roleLower.includes('agent')) return 'success';
      if (roleLower.includes('operator')) return 'info';
      return 'secondary';
    },

    onEdit(item) {
      this.$router.push({
        path: `/admins/User/${item.id}`,
      });
    },

    addNew() {
      this.$router.push({ path: "/admins/User" });
    },

    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },

    onDeleteConfirmation() {
      this.api
        .deleteProfile(this.itemToDelete.id)
        .then((response) => {
          this.refreshTable();
          this.toast("Success", "User deleted successfully", "success");
          this.warningModal = false;
          this.itemToDelete = {};
        })
        .catch((error) => {
          this.toast("Error", "Failed to delete user", "danger");
          this.warningModal = false;
        });
    },

    exportCSV() {
      const csvData = this.items.map((item, index) => ({
        '#': index + 1,
        'Full Name': item.fullName || '',
        'Email': item.email || '',
        'Phone': item.phone || '',
        'Role': item.role || '',
        'Sub Company': item.subCompanyName || (item.subCompany && item.subCompany.name) || '',
      }));

      const headers = Object.keys(csvData[0]);
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `users_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.toast("Success", "CSV exported successfully", "success");
    },

    exportPDF() {
      // Create a temporary element for PDF generation
      const element = document.createElement('div');
      element.style.padding = '20px';
      element.style.fontFamily = 'Arial, sans-serif';

      // Add title and timestamp
      const title = document.createElement('h2');
      title.textContent = 'User Management Report';
      title.style.textAlign = 'center';
      title.style.marginBottom = '10px';
      element.appendChild(title);

      const timestamp = document.createElement('p');
      timestamp.textContent = `Generated on: ${new Date().toLocaleString()}`;
      timestamp.style.textAlign = 'center';
      timestamp.style.color = '#666';
      timestamp.style.marginBottom = '20px';
      element.appendChild(timestamp);

      // Create table
      const table = document.createElement('table');
      table.style.width = '100%';
      table.style.borderCollapse = 'collapse';
      table.style.fontSize = '10px';

      // Create table header
      const thead = document.createElement('thead');
      const headerRow = document.createElement('tr');
      headerRow.style.backgroundColor = '#f8f9fa';

      const headers = ['#', 'Full Name', 'Email', 'Phone', 'Role', 'Sub Company'];
      headers.forEach(headerText => {
        const th = document.createElement('th');
        th.textContent = headerText;
        th.style.border = '1px solid #dee2e6';
        th.style.padding = '8px';
        th.style.textAlign = 'left';
        th.style.fontWeight = 'bold';
        headerRow.appendChild(th);
      });
      thead.appendChild(headerRow);
      table.appendChild(thead);

      // Create table body
      const tbody = document.createElement('tbody');
      this.items.forEach((item, index) => {
        const row = document.createElement('tr');
        if (index % 2 === 0) {
          row.style.backgroundColor = '#ffffff';
        } else {
          row.style.backgroundColor = '#f8f9fa';
        }

        const values = [
          index + 1,
          item.fullName || '',
          item.email || '',
          item.phone || '',
          item.role || '',
          item.subCompanyName || (item.subCompany && item.subCompany.name) || ''
        ];

        values.forEach(value => {
          const td = document.createElement('td');
          td.textContent = value;
          td.style.border = '1px solid #dee2e6';
          td.style.padding = '6px 8px';
          row.appendChild(td);
        });

        tbody.appendChild(row);
      });
      table.appendChild(tbody);
      element.appendChild(table);

      // Add footer with total count
      const footer = document.createElement('p');
      footer.textContent = `Total: ${this.items.length} ${this.items.length === 1 ? 'user' : 'users'}`;
      footer.style.marginTop = '20px';
      footer.style.textAlign = 'right';
      footer.style.color = '#666';
      element.appendChild(footer);

      // PDF generation options
      const options = {
        margin: 10,
        filename: `users_${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { orientation: 'landscape', unit: 'mm', format: 'a4' },
      };

      // Generate and download PDF
      html2pdf()
        .from(element)
        .set(options)
        .save()
        .then(() => {
          this.toast("Success", "PDF exported successfully", "success");
        })
        .catch((error) => {
          console.error("PDF export error:", error);
          this.toast("Error", "Failed to export PDF", "danger");
        });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.user-list-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Main Card */
.main-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Professional Header */
.professional-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1.25rem 1.5rem;
}

.header-title h5 {
  font-size: 1.25rem;
  color: var(--cui-body-color, #212529);
  margin-bottom: 0.25rem;
}

.header-title small {
  font-size: 0.875rem;
}

.result-counter {
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state i {
  font-size: 3rem;
  opacity: 0.5;
}

/* Mobile Card View */
.mobile-view {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mobile-card {
  background: white;
  border: 1px solid var(--cui-border-color, #d8dbe0);
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.mobile-card:hover {
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
}

.mobile-card-header {
  padding: 1rem 1.25rem;
  cursor: pointer;
  background: white;
}

.mobile-card-header:hover {
  background: var(--cui-body-bg, #f8f9fa);
}

.user-number {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.user-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--cui-body-color, #212529);
}

.user-details {
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.mobile-card-footer {
  padding: 0.75rem 1.25rem;
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Desktop Table View */
.desktop-view {
  /* Uses CoreUI's default table styling */
}

.professional-table {
  font-size: 0.875rem;
}

.professional-table th {
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.professional-table tbody tr {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.professional-table tbody tr:hover {
  background-color: var(--cui-table-hover-bg, rgba(0, 0, 0, 0.075));
}

/* Footer */
.card-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.5rem;
}

.footer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .user-list-wrapper {
    padding: 1rem;
  }

  .professional-header {
    padding: 1rem;
  }

  .header-title h5 {
    font-size: 1.125rem;
  }

  .card-footer {
    padding: 1rem;
  }

  .footer-actions {
    width: 100%;
  }

  .footer-actions .btn {
    flex: 1;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .card-footer,
  .mobile-card-footer,
  .show_details {
    display: none !important;
  }

  .mobile-view {
    display: none !important;
  }

  .desktop-view {
    display: block !important;
  }
}
</style>
